import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import 'dayjs/locale/es'; // Importa el idioma que necesitas, en este caso, español
import psl from 'psl';
import duration from 'dayjs/plugin/duration';
import { states } from 'config';
import { object } from 'is_js';

// Cargar los plugins
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);
// Establecer el idioma (opcional, ya que el navegador generalmente establece el idioma automáticamente)
dayjs.locale('es'); // 'es' es para español

export const formattedDate = (date = '', format = 'dddd, MMMM D, YYYY h:mm A') => {
 // Obtener la zona horaria local del navegador
 const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

 // Formatear la fecha y hora actual en la zona horaria local
 return dayjs(date).tz(localTimezone).format(format);
};

export const formErrorMessages = errors => {
 return Object.entries(errors).reduce((acc, prop) => {
  // eslint-disable-next-line no-unused-vars
  const { ref, ...rest } = acc;
  if (typeof rest[prop] === object) rest[prop] = this.formErrorMessages(rest[prop]);
  acc = rest;
  return acc;
 }, errors);
};

export const isIterableArray = array => Array.isArray(array) && !!array.length;

//===============================
// Convertir objeto en un FormData
//===============================
const hasOwnProperty = Object.prototype.hasOwnProperty;

export const objectToFormData = (obj, form, namespace) => {
 const formData = form || new FormData();

 for (const property in obj) {
  if (hasOwnProperty.call(obj, property)) {
   const formKey = namespace ? `${namespace}[${property}]` : property;

   if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
    objectToFormData(obj[property], formData, formKey);
   } else {
    formData.append(formKey, obj[property]);
   }
  }
 }

 return formData;
};

//===============================
// Breakpoints
//===============================
export const breakpoints = {
 xs: 0,
 sm: 576,
 md: 768,
 lg: 992,
 xl: 1200,
 xxl: 1540
};

export const getItemFromStore = (key, defaultValue, store = localStorage) => {
 try {
  return store.getItem(key) === null ? defaultValue : JSON.parse(store.getItem(key));
 } catch {
  return store.getItem(key) || defaultValue;
 }
};

export const setItemToStore = (key, payload, store = localStorage) => store.setItem(key, JSON.stringify(payload));

export const getStoreSpace = (store = localStorage) =>
 parseFloat((escape(encodeURIComponent(JSON.stringify(store))).length / (1024 * 1024)).toFixed(2));

//===============================
// Cookie
//===============================
export const getCookieValue = name => {
 const value = document.cookie.match('(^|[^;]+)\\s*' + name + '\\s*=\\s*([^;]+)');
 return value ? value.pop() : null;
};

export const createCookie = (name, value, cookieExpireTime) => {
 const date = new Date();
 date.setTime(date.getTime() + cookieExpireTime);
 const expires = '; expires=' + date.toUTCString();
 document.cookie = name + '=' + value + expires + '; path=/';
};

/* export const numberFormatter = (number, fixed = 2) => {
  // Nine Zeroes for Billions
  return Math.abs(Number(number)) >= 1.0e9
    ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + 'B'
    : // Six Zeroes for Millions
    Math.abs(Number(number)) >= 1.0e6
    ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + 'M'
    : // Three Zeroes for Thousands
    Math.abs(Number(number)) >= 1.0e3
    ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + 'K'
    : Math.abs(Number(number)).toFixed(fixed);
}; */

export const formatNumber = number => {
 // Nine Zeroes for Billions
 return (
  Math.abs(Number(number)) >= 1.0e9
   ? Math.round(Math.abs(Number(number)) / 1.0e9) + 'B'
   : // Six Zeroes for Millions
   Math.abs(Number(number)) >= 1.0e6
   ? Math.round(Math.abs(Number(number)) / 1.0e6) + 'M'
   : // Three Zeroes for Thousands
   Math.abs(Number(number)) >= 1.0e3
   ? Math.round(Math.abs(Number(number)) / 1.0e3) + 'K'
   : Math.round(Math.abs(Number(number))) + ' '
 ).replace(/\.00/g, '');
};

export const numberFormatter = (number, fixed = 2, arr = false) => {
 // Nine Zeroes for Billions
 let num = (
  Math.abs(Number(number)) >= 1.0e9
   ? (Math.abs(Number(number)) / 1.0e9).toFixed(fixed) + ' B'
   : // Six Zeroes for Millions
   Math.abs(Number(number)) >= 1.0e6
   ? (Math.abs(Number(number)) / 1.0e6).toFixed(fixed) + ' M'
   : // Three Zeroes for Thousands
   Math.abs(Number(number)) >= 1.0e3
   ? (Math.abs(Number(number)) / 1.0e3).toFixed(fixed) + ' K'
   : Math.abs(Number(number)).toFixed(fixed) + ' '
 ).split(/\.| /g);
 if (arr) return num;
 else return num.join('.');
};

//===============================
// Colors
//===============================
export const hexToRgb = hexValue => {
 let hex;
 hexValue.indexOf('#') === 0 ? (hex = hexValue.substring(1)) : (hex = hexValue);
 // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
 const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
 const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(
  hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b)
 );
 return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : null;
};

export const rgbColor = (color = colors[0]) => `rgb(${hexToRgb(color)})`;
export const rgbaColor = (color = colors[0], alpha = 0.5) => `rgba(${hexToRgb(color)},${alpha})`;

export const colors = [
 '#2c7be5',
 '#00d97e',
 '#e63757',
 '#39afd1',
 '#fd7e14',
 '#02a8b5',
 '#727cf5',
 '#6b5eae',
 '#ff679b',
 '#f6c343'
];

export const themeColors = {
 primary: '#2c7be5',
 secondary: '#748194',
 success: '#00d27a',
 info: '#27bcfd',
 warning: '#f5803e',
 danger: '#e63757',
 light: '#f9fafd',
 dark: '#0b1727'
};

export const grays = {
 white: '#fff',
 100: '#f9fafd',
 200: '#edf2f9',
 300: '#d8e2ef',
 400: '#b6c1d2',
 500: '#9da9bb',
 600: '#748194',
 700: '#5e6e82',
 800: '#4d5969',
 900: '#344050',
 1000: '#232e3c',
 1100: '#0b1727',
 black: '#000'
};

export const darkGrays = {
 white: '#fff',
 1100: '#f9fafd',
 1000: '#edf2f9',
 900: '#d8e2ef',
 800: '#b6c1d2',
 700: '#9da9bb',
 600: '#748194',
 500: '#5e6e82',
 400: '#4d5969',
 300: '#344050',
 200: '#232e3c',
 100: '#0b1727',
 black: '#000'
};

export const getGrays = isDark => (isDark ? darkGrays : grays);

export const rgbColors = colors.map(color => rgbColor(color));
export const rgbaColors = colors.map(color => rgbaColor(color));

export const getColor = (name, dom = document.documentElement) => {
 return getComputedStyle(dom).getPropertyValue(`--falcon-${name}`).trim();
};

//===============================

// Echarts
//===============================
export const getPosition = (pos, params, dom, rect, size) => ({
 top: pos[1] - size.contentSize[1] - 10,
 left: pos[0] - size.contentSize[0] / 2
});
//===============================
// E-Commerce
//===============================
export const calculateSale = (base, less = 0, fix = 2) => (base - base * (less / 100)).toFixed(fix);
export const getTotalPrice = (cart, baseItems) =>
 cart.reduce((accumulator, currentValue) => {
  const { id, quantity } = currentValue;
  const { price, sale } = baseItems.find(item => item.id === id);
  return accumulator + calculateSale(price, sale) * quantity;
 }, 0);
export const getSubtotal = items => items.reduce((acc, curr) => curr.price * curr.quantity + acc, 0);
export const getDiscountPrice = (total, discount) => total - total * (discount / 100);

export const getProductsQuantity = products => products.reduce((acc, product) => product.quantity + acc, 0);

//===============================
// Helpers
//===============================
export const getPaginationArray = (totalSize, sizePerPage) => {
 const noOfPages = Math.ceil(totalSize / sizePerPage);
 const array = [];
 let pageNo = 1;
 while (pageNo <= noOfPages) {
  array.push(pageNo);
  pageNo = pageNo + 1;
 }
 return array;
};

export const capitalize = str => (str.charAt(0).toUpperCase() + str.slice(1)).replace(/-/g, ' ');

export const camelize = str => {
 return str.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, function (match, index) {
  if (+match === 0) return ''; // or if (/\s+/.test(match)) for white spaces
  return index === 0 ? match.toLowerCase() : match.toUpperCase();
 });
};

export const dashed = str => {
 return str.toLowerCase().replaceAll(' ', '-');
};

// urls helper

export const generateRegexFromUrls = urls => {
 // Ejemplo de uso -- urls = ['/:company?/project/details/:id/:business?', '/users/:username', '/category/:category/posts/:id'];

 // Creamos un array para almacenar las expresiones regulares resultantes
 const regexArray = urls.map(url => {
  // Dividimos la URL en partes separadas por "/"
  const parts = url
   .split('/')
   .filter(e => e)
   .map(part => {
    // Si la parte termina con "?", la tratamos como un parámetro opcional
    if (part.endsWith('?')) {
     // Eliminamos tanto el prefijo ":" como el sufijo "?"
     //const paramName = part.slice(1, -1);
     // Usamos la expresión regular para cualquier cadena de caracteres alfanuméricos o guiones (opcional) con una barra inclinada opcional al inicio
     return `(\\/[\\w-]+)?`;
    }
    // Si la parte comienza con ":", la tratamos como un parámetro dinámico (obligatorio)
    else if (part.startsWith(':')) {
     // Eliminamos el prefijo ":"
     //const paramName = part.substring(1);
     // Usamos la expresión regular para cualquier cadena de caracteres alfanuméricos o guiones (obligatorio) con una barra inclinada obligatoria al inicio
     return `(\\/[\\w-]+)`;
    } else {
     // Si no es un parámetro, devolvemos la parte tal cual, agregando una barra inclinada al inicio
     return `\\/${part}`;
    }
   });
  // Unimos las partes nuevamente en una expresión regular completa
  const regexString = `^${parts.join('')}$`;
  // Creamos un objeto RegExp con la expresión regular resultante
  return new RegExp(regexString);
 });

 // Devolvemos el array de expresiones regulares
 return regexArray;
};

//routes helper
// funcion setRoutesForCompany ya no sera mas requerida, en revision para ser eliminada
export const setRoutesForCompany = (routes = [], company) => {
 const setRoutes = routes => {
  routes.forEach(route => {
   if (route?.to) route.to = route.to.replace('/1/', `/${company}/`);
   if (route.children) {
    setRoutes(route.children);
   }
  });
 };
 setRoutes(routes);
 return routes;
};

export const flatRoutes = childrens => {
 const allChilds = [];

 const flatChild = childrens => {
  childrens.forEach(child => {
   if (child.children) {
    flatChild(child.children);
   } else {
    allChilds.push(child);
   }
  });
 };
 flatChild(childrens);

 return allChilds;
};

export const setRoute = paths => {
 const flatChild = childrens => {
  childrens.forEach(child => {
   //child.active = false;
   if (child.children) {
    flatChild(child.children);
   }
  });
 };
 const routes = paths.map(e => e);
 flatChild(routes);
 return routes;
};

export const getFlatRoutes = children =>
 children.reduce(
  (acc, val) => {
   if (val.children) return { ...acc, [camelize(val.name)]: flatRoutes(val.children) };
   else return { ...acc, Otros: [...acc.Otros, val] };
  },
  { Otros: [] }
 );

export const routesSlicer = ({ routes, columns = 3, rows }) => {
 const routesCollection = [];
 routes.map(route => {
  if (route.children) {
   return route.children.map(item => {
    if (item.children) {
     return routesCollection.push(...item.children);
    }
    return routesCollection.push(item);
   });
  }
  return routesCollection.push(route);
 });

 const totalRoutes = routesCollection.length;
 const calculatedRows = rows || Math.ceil(totalRoutes / columns);
 const routesChunks = [];
 for (let i = 0; i < totalRoutes; i += calculatedRows) {
  routesChunks.push(routesCollection.slice(i, i + calculatedRows));
 }
 return routesChunks;
};

// end routes

export const getPageName = pageName => {
 return window.location.pathname.split('/').slice(-1)[0] === pageName;
};

export const copyToClipBoard = textFieldRef => {
 const textField = textFieldRef.current;
 textField.focus();
 textField.select();
 document.execCommand('copy');
};

export const reactBootstrapDocsUrl = 'https://react-bootstrap.github.io';

export const pagination = (currentPage, size) => {
 const pages = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
 let prev = currentPage - 1 - Math.floor(size / 2);

 if (currentPage - 1 - Math.floor(size / 2) < 0) {
  prev = 0;
 }
 if (currentPage - 1 - Math.floor(size / 2) > pages.length - size) {
  prev = pages.length - size;
 }
 const next = prev + size;

 return pages.slice(prev, next);
};

export const tooltipFormatter = params => {
 let tooltipItem = ``;
 params.forEach(el => {
  tooltipItem =
   tooltipItem +
   `<div class='ms-1'> 
        <h6 class="text-700"><span class="fas fa-circle me-1 fs--2" style="color:${
         el.borderColor ? el.borderColor : el.color
        }"></span>
          ${el.seriesName} : ${typeof el.value === 'object' ? el.value[1] : el.value}
        </h6>
      </div>`;
 });
 return `<div>
            <p class='mb-2 text-600'>
              ${dayjs(params[0].axisValue).isValid() ? dayjs(params[0].axisValue).format('MMMM DD') : params[0].axisValue}
            </p>
            ${tooltipItem}
          </div>`;
};

export const addIdField = items => {
 return items.map((item, index) => ({
  id: index + 1,
  ...item
 }));
};

// get file size

export const getSize = size => {
 if (size < 1024) {
  return `${size} Byte`;
 } else if (size < 1024 * 1024) {
  return `${(size / 1024).toFixed(2)} KB`;
 } else {
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
 }
};

/* Get A Random Number */
export const getRandomNumber = (min, max) => {
 return Math.floor(Math.random() * (max - min) + min);
};

/* get Dates between */

export const getDates = (startDate, endDate, interval = 1000 * 60 * 60 * 24) => {
 const duration = endDate - startDate;
 const steps = duration / interval;
 return Array.from({ length: steps + 1 }, (v, i) => new Date(startDate.valueOf() + interval * i));
};

/* Get Past Dates */
export const getPastDates = duration => {
 let days;

 switch (duration) {
  case 'week':
   days = 7;
   break;
  case 'month':
   days = 30;
   break;
  case 'year':
   days = 365;
   break;

  default:
   days = duration;
 }

 const date = new Date();
 const endDate = date;
 const startDate = new Date(new Date().setDate(date.getDate() - (days - 1)));
 return getDates(startDate, endDate);
};

// Add id to items in array
export const addId = items =>
 items.map((item, index) => ({
  id: index + 1,
  ...item
 }));

//
export const getTimeDuration = (startDate, endDate, format = '') => {
 return dayjs.duration(endDate.diff(startDate)).format(format);
};

// Get Percentage
export const getPercentage = (number, percent) => {
 return (Number(number) / 100) * Number(percent);
};

//get chunk from array
export const chunk = (arr, chunkSize = 1, cache = []) => {
 const tmp = [...arr];
 if (chunkSize <= 0) return cache;
 while (tmp.length) cache.push(tmp.splice(0, chunkSize));
 return cache;
};

export const validateURL = url => {
 try {
  new URL('https://' + url);
  return true;
 } catch (err) {
  return false;
 }
};

const parseDomain = (data = []) => {
 try {
  return data[1].replace(/^\./, '');
 } catch (e) {
  return null;
 }
};

export const checkDomain = async origin => {
 try {
  // eslint-disable-next-line
  const re = /^(?:https?:)?(?:\/\/)?(?:[^@\n]+@)?(?:www\.)?(\.?[^:\/\n]+)/gi;
  const result = re.exec(origin);
  const rawDomain = parseDomain(result);
  console.log(rawDomain, origin);
  return psl.parse(rawDomain);
 } catch (e) {
  console.log(e, 'Error en checkDomain');
 }
};

export const convertToNumber = input => {
 if (isNaN(parseFloat(input))) return 0;
 return Math.max(0, parseFloat(input));
};

const defaultCurrencyData = { amount: 0, symbol: false, multiply: false, denial: false, cents: false, decimals: 2 };

export const changeToNumber = currencyData => {
 // Se asegura de que currencyData contenga valores predeterminados de defaultCurrencyData
 currencyData = { ...defaultCurrencyData, ...currencyData };

 // Si no hay cantidad, retorna 0
 if (!currencyData.amount) return 0;

 const { amount, cents } = currencyData;

 // Convierte la cantidad a número, eliminando cualquier caracter que no sea dígito, punto o guión
 const value = /[^0-9.-]/g.test(amount) ? amount.toString().replace(/[^0-9.-]/g, '') || 0 : amount;

 // Verifica si hay un punto seguido de un símbolo de porcentaje o un punto final
 const punto = /\s%\.$|\.%$|\.$/.test(amount);

 // Verifica si la cantidad es exactamente 0.0, 0.00 o 0.000
 const isZero = /^0\.0{1,3}(\s?%|)$/.test(value) ? value : null;

 // Si se requiere centavos, multiplica por 100
 const num = cents ? parseFloat(value) * 100 : parseFloat(value);

 // Verifica si el resultado no es un número
 if (typeof num !== 'number') throw TypeError('El argumento no puede ser de tipo string');

 const result = punto ? num + '.' : num;

 //  console.log({ isZero, punto, value, amount, num, result }, isZero ?? result);

 return isZero ?? result;
};

export const changeToCoin = currencyData => {
 currencyData = { ...defaultCurrencyData, ...currencyData };

 if (!currencyData.amount) return currencyData.amount;

 let { amount, cents, denial, multiply, decimals, symbol } = currencyData;

 amount = /[^0-9.]/g.test(amount) ? amount.toString().replace(/[^0-9.]/g, '') : amount;

 if (!amount) return 0;

 const punto = /\.$/.test(amount);

 const isZero = /^0\.0{1,3}(\s?%|)$/.test(amount) ? amount + ' %' : null;

 if (denial && /^-/g.test(currencyData.amount)) denial = true;
 else denial = false;

 const num = cents ? parseFloat(amount) / 100 : parseFloat(amount);

 if (typeof num !== 'number') throw TypeError('El argumento no puede ser de tipo string');

 //  if (symbol) symbol = (num >= 1 && !cents) || (num >= 100 && cents) ? '$ ' : num > 0 ? ' %' : '';
 if (symbol) symbol = symbol = num > 0 && num < 1 ? ' %' : num > 0 ? '$ ' : '';

 const number = num.toLocaleString('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: symbol === ' %' ? 4 : decimals
 });

 //  console.log({ number, symbol, amount, num });

 const value = punto ? number + '.' : number;
 const amountDenial = denial ? '-' + value : value;
 const result =
  symbol === ' %' && multiply
   ? Math.round((amountDenial * 100 + Number.EPSILON) * 100) / 100 + symbol
   : symbol === ' %'
   ? amountDenial + symbol
   : denial
   ? '-' + symbol + value
   : symbol + value;

 return isZero ?? result;
};

export const setStatus = status => {
 const std = parseInt(status) || 'und';
 return states.find(e => e.val === std || !e.val);
};

export const currency = (value, coin = false, mul = false, neg = false) => {
 if (!value) return value;

 if (!/[-0-9$]/g.test(value)) return 0;
 if (/\d\s$/g.test(value) && value < 1) value = value.slice(0, -2);
 if (!value || !/[0-9]/g.test(value)) return value;
 let symbol = '';
 const punto = /\s%\.$|\.%$|\.$/.test(value);

 if (neg && /^-/g.test(value)) neg = true;
 else neg = false;
 const num = /[^0-9.]/g.test(value) ? parseFloat(value.toString().replace(/[^0-9.]/g, '')) : parseFloat(value);
 if (typeof num != 'number') throw TypeError('El argumento no puede ser de tipo string');

 if (coin) symbol = num > 0 && num < 1 ? ' %' : num > 0 ? '$ ' : '';

 const number = num.toLocaleString('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: num < 1 ? 4 : 2
 });
 const amount = punto ? number + '.' : number;
 const amount2 = neg ? '-' + amount : amount;
 const result =
  symbol === ' %' && mul
   ? Math.round((amount2 * 100 + Number.EPSILON) * 100) / 100 + symbol
   : symbol === ' %'
   ? amount2 + symbol
   : neg
   ? '-' + symbol + amount
   : symbol + amount;

 console.log({ result });
 return result;
};

export const noCurrency = value => {
 if (!value) return 0;
 const num = /[^0-9.-]/g.test(value) ? parseFloat(value.replace(/[^0-9.-]/g, '') || 0) : parseFloat(value);
 if (typeof num != 'number') throw TypeError('El argumento no puede ser de tipo string');
 return num;
};

export const percent = (value, symbol = true, maxDigits = 10) => {
 if (!value) return [0, symbol ? '0 %' : 0];
 if (/\d\s$/g.test(value)) value = value.slice(0, -2);
 if (!value) return [0, ''];
 if (!/[0-9]/g.test(value)) return [0, symbol ? '0 %' : 0];

 const punto = /\s%\.$|\.$/.test(value);
 const num = /[^0-9.-]/g.test(value) ? parseFloat(value.replace(/[^0-9.]/g, '')) : parseFloat(value);
 if (typeof num != 'number') throw TypeError('El argumento no puede ser de tipo string');

 symbol = num > 0 && symbol;

 const number = num.toLocaleString('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: maxDigits
 });

 const amount = punto ? number + '.' : number;

 return [amount / 100, symbol ? amount + ' %' : amount];
};

export const numCard = value => {
 if (/[^0-9 ]/g.test(value)) return value.replace(/[^0-9 ]/g, '');
 const arrayValues = value
  .replace(/[^0-9]/g, '')
  .split(/(\d{4})/, 8)
  .filter(e => e);

 return arrayValues.join(' ');
};

export const dateCard = value => {
 if (!value) return '';
 const cleanedValue = value.replace(/[^0-9]/g, '');
 return cleanedValue.replace(/(\d{2})(\d{2})/, '$1/$2');
 /* if (/[^0-9/]/g.test(value)) return value.replace(/[^0-9/]/g, '');
 const arrayValues = value
  .replace(/[^0-9]/g, '')
  .split(/(\d{2})/, 4)
  .filter(e => e);

 return arrayValues.join('/'); */
};

// Slugify text
export const slugifyText = str =>
 str
  .toLowerCase()
  .replace(/\s+/g, '-')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/--+/g, '-')
  .replace(/^-+/, '')
  .replace(/-+$/, '');

// Definición de las palabras para unidades, decenas y casos especiales
const units = ['', 'un', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
const tens = ['', '', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
const special = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciséis', 'diecisiete', 'dieciocho', 'diecinueve'];
const hundred = [
 '',
 (e = false) => (e ? 'ciento' : 'cien'),
 'docientos',
 'trecientos',
 'catorcientos',
 'quinientos',
 'seiscientos',
 'setecientos',
 'ochocientos',
 'novecientos'
];

export const numberToWords = num => {
 if (num === 0) return 'cero pesos';
 if (!num) return '...';

 // Separamos el número en millones, miles y centenas
 //  const trillions = Math.floor(num / 1000000000000); // Billones
 const billions = Math.floor(num / 1000000000000); // Billones
 const thousandsOfBillions = Math.floor((num % 1000000000000) / 1000000000); // Miles de millones
 const millions = Math.floor((num % 1000000000) / 1000000); // Millones
 const thousands = Math.floor((num % 1000000) / 1000); // Miles
 const hundreds = Math.floor(num % 1000); // Centenas

 // Variable para almacenar el resultado
 let result = '';

 // Función para convertir un número en palabras
 const convertNumber = number => {
  if (number === 0) return '';
  if (number < 10) return units[number];
  if (number < 20) return special[number - 10];

  if (number < 100) {
   const ten = Math.floor(number / 10);
   const unit = number % 10;
   return `${tens[ten]}${!ten || !unit ? units[unit] : unit === 1 ? ' y uno' : ' y ' + units[unit]}`;
  }

  const hun = Math.floor(number / 100);
  const num = number % 100;
  const ten = Math.floor(num / 10);
  const unit = num % 10;

  return `${hun === 1 ? hundred[hun](ten || unit) : hundred[hun]} ${tens[ten]}${
   !ten || !unit ? units[unit] : unit === 1 ? ' y uno' : ' y ' + units[unit]
  }`;
 };

 // Convertir billones si es mayor que cero
 if (billions > 0) result += convertNumber(billions) + (billions < 2 ? ' billón ' : ' billones ');

 // Convertir miles de millones si es mayor que cero
 if (thousandsOfBillions > 0) result += convertNumber(thousandsOfBillions) + ' mil ';

 // Convertir millones si es mayor que cero
 if (millions > 0) result += convertNumber(millions) + (millions < 2 ? ' millón ' : ' millones ');

 // Convertir miles si es mayor que cero
 if (thousands > 0) result += convertNumber(thousands) + ' mil ';

 // Convertir centenas si es mayor que cero
 if (hundreds > 0) result += convertNumber(hundreds);

 // Agregar "de pesos" o "pesos" dependiendo del valor
 result += thousands || hundreds ? ' pesos' : ' de pesos';

 // Agregar centavos si corresponde
 const cents = Math.round((num - Math.floor(num)) * 100);
 if (cents > 0) {
  result += ' con ' + convertNumber(cents) + ' centavos';
 }

 return result;
};
